import React from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../UI/card';
import { Badge } from '../UI/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../UI/avatar';
import { Button } from '../UI/button';
import { MapPin, Linkedin } from 'lucide-react';
import { useCurrency } from '../../contexts/CurrencyContext';

interface ProfessionalCardProps {
  professional: {
    id: string;
    userId: string;
    firstName: string;
    lastName: string;
    profilePicture?: string;
    industry: string;
    seniority: string;
    position?: string;
    company?: string;
    headline?: string;
    expertise: string[];
    hourlyRate: number;
    rateCurrency?: string;
    bio: string;
    isVerified: boolean;
    rating: number;
    totalSessions: number;
    location?: string;
    linkedinUrl?: string;
  };
  onRequestChat?: (id: string) => void;
  onViewProfile?: (id: string) => void;
  onSendInvitation?: () => void;
}

const ProfessionalCard: React.FC<ProfessionalCardProps> = ({
  professional,
  onRequestChat,
  onViewProfile,
  onSendInvitation
}) => {
  const {
    id,
    firstName,
    lastName,
    profilePicture,
    industry,
    seniority,
    position,
    company,
    headline,
    expertise,
    hourlyRate,
    rateCurrency,
    location,
    linkedinUrl
  } = professional;

  // Generate display headline: use saved headline, or fallback to "position at company"
  const displayHeadline = headline ||
    (position && company ? `${position} at ${company}` :
     position ? position :
     company ? `Professional at ${company}` :
     seniority ? `${seniority} Professional` : '');

  const fullName = `${firstName} ${lastName}`;
  const getInitials = () => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase() || '??';
  };

  // Use currency context for conversion
  const { currency, rates, getCurrencySymbol } = useCurrency();

  // Convert from professional's base currency to user's selected display currency
  const convertRate = (rate: number, fromCurrency: string, toCurrency: string): number => {
    if (fromCurrency === toCurrency) return rate;

    // Exchange rates (approximate)
    const toGBP: { [key: string]: number } = { GBP: 1, USD: 1/1.27, EUR: 1/1.17 };
    const fromGBP: { [key: string]: number } = { GBP: 1, USD: 1.27, EUR: 1.17 };

    // Convert to GBP first, then to target currency
    const rateInGBP = rate * toGBP[fromCurrency];
    return rateInGBP * fromGBP[toCurrency];
  };

  const baseCurrency = rateCurrency || 'GBP';
  const displayRate = Math.round(convertRate(hourlyRate, baseCurrency, currency));

  // Safely handle expertise array
  const expertiseArray = Array.isArray(expertise) ? expertise : [];

  return (
    <Card className="hover:shadow-lg transition-shadow">
      <CardContent className="p-4 sm:p-6">
        <div className="flex flex-col md:flex-row gap-4 md:gap-6">
          {/* Top Section - Avatar + Name/Headline + Price (mobile) / Left-Center Section (desktop) */}
          <div className="flex-1 min-w-0 space-y-3">
            {/* Avatar and Name Row */}
            <div className="flex items-start gap-3 sm:gap-4">
              <div className="flex-shrink-0">
                <Avatar className="h-16 w-16 sm:h-20 sm:w-20">
                  <AvatarImage src={profilePicture} alt={fullName} />
                  <AvatarFallback className="bg-blue-600 text-white text-lg sm:text-xl">
                    {getInitials()}
                  </AvatarFallback>
                </Avatar>
              </div>

              <div className="flex-1 min-w-0">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                  {fullName}
                </h3>
                {displayHeadline && (
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5 line-clamp-2">
                    {displayHeadline}
                  </p>
                )}
              </div>
            </div>

            {/* Bio */}
            {professional.bio && (
              <p className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 line-clamp-2 md:line-clamp-3">
                {professional.bio}
              </p>
            )}

            {/* Expertise Badges */}
            <div className="flex flex-wrap gap-1.5 sm:gap-2">
              {expertiseArray.slice(0, 5).map((skill, index) => (
                <Badge key={skill || index} variant="secondary" className="text-xs">
                  {skill}
                </Badge>
              ))}
              {expertiseArray.length > 5 && (
                <Badge variant="secondary" className="text-xs">
                  +{expertiseArray.length - 5}
                </Badge>
              )}
            </div>
          </div>

          {/* Pricing & Actions - Right column on desktop, below content on mobile */}
          <div className="flex-shrink-0 flex flex-col md:items-end gap-3 md:min-w-[200px]">
            <div className="text-left md:text-right">
              <div className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                {getCurrencySymbol()}{displayRate}
              </div>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
                for 30-min session
              </p>
            </div>

            {/* Action Buttons - Stack vertically on mobile, horizontal on md+ */}
            <div className="flex flex-col md:flex-row gap-2 w-full">
              {linkedinUrl && (
                <Button
                  variant="outline"
                  className="flex-1 text-sm sm:text-base py-2"
                  onClick={() => window.open(linkedinUrl, '_blank', 'noopener,noreferrer')}
                  aria-label={`View ${fullName}'s LinkedIn profile`}
                >
                  <Linkedin className="h-4 w-4 mr-2" />
                  LinkedIn
                </Button>
              )}
              <Button
                className="flex-1 text-sm sm:text-base py-2"
                onClick={() => onSendInvitation?.() || onRequestChat?.(id)}
              >
                Send Invitation
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProfessionalCard; 